import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { WidgetStatus } from "~/components/widgetStatus/WidgetStatus";
import { routes } from "~/router";

import { SiteWidget } from "../../../../api/Api";
import { apiClient } from "../../../../api/ApiClient";
import JavaScriptSnippet from "../../../javaScriptSnippet/JavaScriptSnippet";
import NotificationBlock from "../../../notificationBlock/NotificationBlock";
import Button from "../../../ui/button/Button";
import Modal from "../../../ui/modal/Modal";
import { tabs } from "../NewSite";
import ContactDeveloper from "../contactDeveloper/ContactDeveloper";
import styles from "./NewSiteAutomations.module.scss";

type NewSiteAutomationsProps = {
  widgetDetail: SiteWidget | undefined;
  newSiteId: number;
  setActiveTab: (tab: string) => void;
  backTab?: string;
};
const NewSiteAutomations = ({
  widgetDetail,
  newSiteId,
  setActiveTab,
  backTab = tabs[3],
}: NewSiteAutomationsProps) => {
  const navigate = useNavigate();
  const [openModal, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["testWidget"],
    queryFn: async () => {
      const response = await apiClient.sites.widgetDetail(newSiteId);

      return response;
    },
    enabled: !!newSiteId,
    refetchInterval(query) {
      if (
        query.state.data?.data?.site_widget &&
        query.state.data?.data?.site_widget?.widget_installed
      ) {
        return false;
      }

      return 15000;
    },
  });
  const widgetInstalled = data?.data?.site_widget?.widget_installed;

  const widgetCode = `<script type="text/javascript" src="${widgetDetail?.widget_data_uri}"></script>`;
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
  };
  const resetNewSiteId = () => {
    navigate(routes.site.root({ id: newSiteId }));
    localStorage.removeItem("siteId");
  };

  return (
    <>
      <div className={styles.content}>
        <h2 className={styles.title}>Automations</h2>
        <div className={styles.description}>
          If you want automations and optimizations to be available for human
          visitors, a JavaScript snippet needs to be added to your website.
        </div>
        <ol className={styles.widgetCodeList}>
          <li>Copy the code snippet below.</li>
          <li>{`Paste it above the closing </HEAD> of your site's source code or template.`}</li>
          <li>
            Make sure the snippet is present on every page where you want to
            receive search engine traffic.
          </li>
          <li>
            That's it! You really just have to copy and paste this little code
            ;)
          </li>
        </ol>

        <div className={styles.widgetCodeNotification}>
          <NotificationBlock
            type="info"
            message="Note! The only changes to your site will be from Recommendations you review and approve."
          />
        </div>
        <div>
          <JavaScriptSnippet
            code={widgetCode}
            actions={
              <Button outlined onClick={handleOpen}>
                Email Instructions
              </Button>
            }
          />
        </div>
      </div>
      <div className={styles.footer}>
        <Button
          outlined
          onClick={() => {
            setActiveTab(backTab);
            navigate(backTab);
          }}
        >
          Back
        </Button>
        <div className={styles.footerButtons}>
          {typeof widgetInstalled === "boolean" && (
            <WidgetStatus widgetInstalled={widgetInstalled} />
          )}

          <Button onClick={resetNewSiteId} formId="newSiteDetailsForm">
            {!widgetInstalled ? "Skip" : "Finished"}
          </Button>
        </div>
      </div>
      <Modal
        key="contactDeveloper"
        isOpen={openModal}
        closeModal={handleClose}
        buttonLabel="Send Email"
        formId="contactDeveloper"
        modalLabel={<h3 className={styles.modalTitle}>Contact Developer</h3>}
      >
        <ContactDeveloper newSiteId={newSiteId} handleClose={handleClose} />
      </Modal>
    </>
  );
};
export default NewSiteAutomations;
